import {
  Box,
  Paper,
  Typography,
  Collapse,
  TextField,
  InputAdornment,
  CircularProgress,
  Chip
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import SearchIcon from '@mui/icons-material/Search'
import { useEffect, useState, useRef } from 'react'
import { toast } from 'react-toastify'
import { MapModule, useBingMaps } from 'hooks/use-bingMaps'
import { type AggregatedRow, type GSRPCapacityReportModel } from 'Pages/Reports/GSRPCapacityReport.types'
import { FacilityDetails } from 'Pages/Reports/FacilityDetails'
import { aggregateReportData, groupByProviderWithFacilities } from 'Pages/Reports/GSRPCapacityReport.utils'

interface MapViewProps {
  reportData: GSRPCapacityReportModel[]
}

export function GSRPCapacityReportMapView (props: MapViewProps): JSX.Element {
  const { reportData } = props
  const { isLoaded, getLocationFromAddress, getMap } = useBingMaps({
    modules: [MapModule.SpatialMath]
  })

  const [expandedProvider, setExpandedProvider] = useState<string | null>(null)
  const [mapPins, setMapPins] = useState<Array<{ pin: Microsoft.Maps.Pushpin, providerName: string, facilityName: string, color: string }>>([])
  const mapInstanceRef = useRef<Microsoft.Maps.Map | null>(null)
  const mapCreatedRef = useRef(false)
  const isdCenterRef = useRef<Microsoft.Maps.Location | null>(null)
  const [providerSearch, setProviderSearch] = useState<string>('')
  const [mapLoading, setMapLoading] = useState(false)
  const [mobileModalContent, setMobileModalContent] = useState<{ facility: AggregatedRow, providerName: string } | null>(null)

  // Reset map when reportData changes
  useEffect(() => {
    if (mapInstanceRef.current !== null) {
      mapInstanceRef.current.entities.clear()
      mapInstanceRef.current = null
      mapCreatedRef.current = false
      setMapPins([])
      setMobileModalContent(null)
    }
  }, [reportData])

  // Create map and add pushpins
  useEffect(() => {
    const renderMap = async (): Promise<void> => {
      if (!isLoaded || reportData.length === 0) {
        return
      }

      // If map already exists, don't recreate it
      if (mapCreatedRef.current) {
        return
      }

      setMapLoading(true)

      // Array to store pin references
      const newPins: Array<{ pin: Microsoft.Maps.Pushpin, providerName: string, facilityName: string, color: string }> = []

      // Get facility-level aggregated data
      const facilities = aggregateReportData(reportData, 'facility')

      // Use ISD address as center location
      const isdAddress = reportData[0]?.isdAddress
      if (isdAddress === undefined || isdAddress === null || isdAddress === '') {
        toast.error('ISD address not available')
        return
      }

      const centerLocation = await getLocationFromAddress({ address: isdAddress })

      if (centerLocation === null) {
        toast.error('Unable to geocode ISD address')
        return
      }

      // Store ISD center for later use
      isdCenterRef.current = centerLocation

      // Ensure map container exists in DOM
      const mapContainer = document.getElementById('map-container')
      if (mapContainer === null) {
        console.error('Map container element not found')
        toast.error('Map container not ready')
        setMapLoading(false)
        return
      }

      // Create map
      const newMap = getMap(centerLocation)

      // Ensure map and entities collection are initialized
      if (newMap === null || newMap === undefined) {
        console.error('Map is null or undefined')
        toast.error('Failed to initialize map')
        setMapLoading(false)
        return
      }

      if (newMap.entities === undefined || newMap.entities === null) {
        console.error('Map entities collection is not initialized')
        toast.error('Failed to initialize map entities')
        setMapLoading(false)
        return
      }

      // Geocode all facilities in parallel
      const geocodePromises = facilities.map(async (facility) => {
        if (facility.facilityAddress !== undefined && facility.facilityAddress !== null && facility.facilityAddress !== '') {
          const location = await getLocationFromAddress({ address: facility.facilityAddress })
          return { facility, location }
        }
        return { facility, location: null }
      })

      const geocodedFacilities = await Promise.all(geocodePromises)

      // Create pins from geocoded results
      for (const { facility, location } of geocodedFacilities) {
        if (location !== null) {
          const utilizationPercent = facility.totalAllocatedCapacity === 0
            ? 0
            : (facility.totalActiveSeats / facility.totalAllocatedCapacity) * 100

          let pinColor = '#4DA4A4' // Green
          if (utilizationPercent >= 100) {
            pinColor = '#E4536B' // Red
          } else if (utilizationPercent >= 80) {
            pinColor = '#F9D423' // Yellow
          }

          // Create pin with default highlighted appearance (no text labels to avoid hitbox issues)
          const pinOptions: Microsoft.Maps.IPushpinOptions = {
            icon: `<svg xmlns="http://www.w3.org/2000/svg" width="30" height="40" viewBox="0 0 24 32">
              <path d="M12 0C7.589 0 4 3.589 4 8c0 5.5 8 16 8 16s8-10.5 8-16c0-4.411-3.589-8-8-8z" fill="${pinColor}" stroke="white" stroke-width="1.5"/>
              <circle cx="12" cy="8" r="3" fill="white"/>
            </svg>`,
            anchor: new Microsoft.Maps.Point(15, 40)
          }

          const pin = new Microsoft.Maps.Pushpin(location, pinOptions)

          // Store pin reference with metadata
          newPins.push({
            pin,
            providerName: facility.providerName,
            facilityName: facility.facilityName ?? '',
            color: pinColor
          })

          // Desktop click: Expand provider in list and scroll to facility
          Microsoft.Maps.Events.addHandler(pin, 'click', (e) => {
            // Only handle on desktop
            if ('ontouchstart' in window || navigator.maxTouchPoints > 0) {
              return
            }

            setExpandedProvider(facility.providerName)

            // Scroll to the facility in the list after a short delay to allow state update
            setTimeout(() => {
              const facilityKey = `${facility.providerName}|${facility.facilityName ?? ''}`
              const facilityElement = document.querySelector(`[data-facility-key="${facilityKey}"]`)
              if (facilityElement !== null) {
                facilityElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
              }
            }, 100)
          })

          // Mobile: Show custom modal overlay
          Microsoft.Maps.Events.addHandler(pin, 'click', (e) => {
            // Only handle on touch devices
            if (!('ontouchstart' in window) && navigator.maxTouchPoints === 0) {
              return
            }

            setMobileModalContent({
              facility,
              providerName: facility.providerName
            })
          })

          // Safely add pin to map
          try {
            if (newMap.entities !== undefined) {
              newMap.entities.push(pin)
            }
          } catch (error) {
            console.error('Error adding pin to map:', error)
          }
        }
      }

      // Store pin references in state and map instance in ref
      setMapPins(newPins)
      mapInstanceRef.current = newMap
      mapCreatedRef.current = true
      setMapLoading(false)
    }

    void renderMap()
  }, [reportData, isLoaded])

  // Update pin styles and center map when selection changes
  useEffect(() => {
    if (mapPins.length === 0 || mapInstanceRef.current === null) return

    // Update pin styles
    mapPins.forEach(({ pin, providerName, color }) => {
      const isHighlighted = expandedProvider === null || providerName === expandedProvider

      if (isHighlighted) {
        // Highlighted: larger, colorful pin
        pin.setOptions({
          icon: `<svg xmlns="http://www.w3.org/2000/svg" width="30" height="40" viewBox="0 0 24 32">
            <path d="M12 0C7.589 0 4 3.589 4 8c0 5.5 8 16 8 16s8-10.5 8-16c0-4.411-3.589-8-8-8z" fill="${color}" stroke="white" stroke-width="1.5"/>
            <circle cx="12" cy="8" r="3" fill="white"/>
          </svg>`,
          anchor: new Microsoft.Maps.Point(15, 40)
        })
      } else {
        // Dimmed: smaller, gray pin
        pin.setOptions({
          icon: `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="27" viewBox="0 0 24 32">
            <path d="M12 0C7.589 0 4 3.589 4 8c0 5.5 8 16 8 16s8-10.5 8-16c0-4.411-3.589-8-8-8z" fill="#999999" opacity="0.5" stroke="white" stroke-width="1"/>
            <circle cx="12" cy="8" r="2.5" fill="white" opacity="0.5"/>
          </svg>`,
          anchor: new Microsoft.Maps.Point(10, 27)
        })
      }
    })

    // Center map based on selection
    if (expandedProvider !== null) {
      // Find first facility for the selected provider
      const firstPin = mapPins.find(p => p.providerName === expandedProvider)
      if (firstPin !== undefined) {
        const location = firstPin.pin.getLocation()
        mapInstanceRef.current.setView({ center: location, zoom: 12 })
      }
    } else {
      // Return to ISD center
      if (isdCenterRef.current !== null) {
        mapInstanceRef.current.setView({ center: isdCenterRef.current, zoom: 10 })
      }
    }
  }, [expandedProvider, mapPins])

  // Get provider-level aggregated data with facilities for list
  const allProviders = groupByProviderWithFacilities(reportData)

  // Filter providers based on search
  const providerList = providerSearch.trim() === ''
    ? allProviders
    : allProviders.filter(provider =>
      provider.providerName.toLowerCase().includes(providerSearch.toLowerCase())
    )

  // Helper function to get capacity status
  const getCapacityStatus = (totalActive: number, capacity: number): { label: string, color: string, textColor?: string } => {
    if (capacity === 0) return { label: 'No Capacity', color: '#999', textColor: 'white' }

    const utilizationPercent = (totalActive / capacity) * 100
    if (utilizationPercent >= 100) {
      return { label: 'No Open Capacity', color: '#E4536B', textColor: 'white' }
    } else if (utilizationPercent >= 80) {
      return { label: 'Limited Capacity', color: '#F9D423', textColor: '#000000' } // Black text for yellow (ADA)
    }
    return { label: 'Capacity Available', color: '#4DA4A4', textColor: 'white' }
  }

  return (
    <Box>
      {/* Two columns: Provider list and Map */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 3
        }}
      >
        {/* Map - full width on mobile, shared width on desktop */}
        <Box
          sx={{
            flex: 1,
            width: { xs: '100%', md: 'auto' },
            minWidth: { xs: '100%', md: '375px' },
            order: { xs: 1, md: 2 },
            position: 'relative'
          }}
        >
          <Box
            id="map-container"
            sx={{
              width: '100%',
              height: { xs: 'calc(100vh - 180px)', md: '600px' },
              borderRadius: 2,
              overflow: 'hidden',
              border: '1px solid #E1E1E1'
            }}
          />
          {mapLoading && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'rgba(255, 255, 255, 0.8)',
                borderRadius: 2,
                zIndex: 1000
              }}
            >
              <Box sx={{ textAlign: 'center' }}>
                <CircularProgress size={40} />
                <Typography variant="body2" sx={{ mt: 2, color: '#666' }}>
                  Loading map...
                </Typography>
              </Box>
            </Box>
          )}

          {/* Mobile modal overlay */}
          {mobileModalContent !== null && (
            <Box
              onClick={() => { setMobileModalContent(null) }}
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: { xs: 'flex', md: 'none' },
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'rgba(0, 0, 0, 0.5)',
                zIndex: 1001
              }}
            >
              <Box
                onClick={(e) => { e.stopPropagation() }}
                sx={{
                  bgcolor: 'white',
                  borderRadius: 2,
                  width: '100%',
                  maxHeight: '80%',
                  overflow: 'auto',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
                }}
              >
                <FacilityDetails
                  facility={mobileModalContent.facility}
                  providerName={mobileModalContent.providerName}
                  getCapacityStatus={getCapacityStatus}
                  showProviderName={true}
                  showAddress={true}
                />
              </Box>
            </Box>
          )}
        </Box>

        {/* Provider list - hidden on mobile, shown on desktop */}
        <Box
          sx={{
            width: { xs: '100%', md: '450px' },
            minWidth: { xs: '100%', md: '375px' },
            order: { xs: 2, md: 1 },
            display: { xs: 'none', md: 'block' }
          }}
        >
          <Paper sx={{ borderRadius: 2, overflow: 'hidden', height: '600px', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ bgcolor: '#F5F5F5', p: 2, borderBottom: '1px solid #E1E1E1' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Providers
              </Typography>
              <TextField
                size="small"
                fullWidth
                placeholder="Search providers..."
                value={providerSearch}
                onChange={(e) => { setProviderSearch(e.target.value) }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  )
                }}
                sx={{
                  bgcolor: 'white',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1
                  }
                }}
              />
            </Box>
            <Box sx={{ flex: 1, overflowY: 'auto' }}>
              {providerList.map((provider, index) => {
                const isExpanded = expandedProvider === provider.providerName
                return (
                  <Box key={index}>
                    {/* Provider summary */}
                    <Box
                      onClick={() => {
                        setExpandedProvider(isExpanded ? null : provider.providerName)
                      }}
                      sx={{
                        p: 2,
                        borderBottom: '1px solid #E1E1E1',
                        borderLeft: isExpanded ? '4px solid #722a82' : '4px solid transparent',
                        bgcolor: 'white',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          bgcolor: '#F0F0F0',
                          cursor: 'pointer'
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {isExpanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                        <Typography variant="body2" sx={{ fontWeight: 'bold', flex: 1 }}>
                          {provider.providerName}
                        </Typography>
                        <Chip
                          label={(() => {
                            const openSeats = Math.max(0, provider.totalAllocatedCapacity - provider.totalActiveSeats)
                            const seeking = provider.countPendingProcessing + provider.countWaitlisted
                            if (openSeats === 0) {
                              return `0 open, ${seeking} seeking`
                            } else if (seeking > openSeats) {
                              return `${openSeats} open, ${seeking} seeking`
                            }
                            return `${openSeats} open`
                          })()}
                          size="small"
                          sx={{
                            bgcolor: getCapacityStatus(provider.totalActiveSeats, provider.totalAllocatedCapacity).color,
                            color: getCapacityStatus(provider.totalActiveSeats, provider.totalAllocatedCapacity).textColor ?? 'white',
                            fontWeight: 'bold',
                            fontSize: '11px'
                          }}
                        />
                      </Box>
                    </Box>

                    {/* Facilities - shown when expanded */}
                    <Collapse in={isExpanded}>
                      <Box sx={{ bgcolor: '#FAFAFA', pl: 2 }}>
                        {provider.facilities.map((facility, facilityIndex) => {
                          const facilityKey = `${provider.providerName}|${facility.facilityName ?? ''}`

                          return (
                            <Box key={facilityIndex} data-facility-key={facilityKey}>
                              <FacilityDetails
                                facility={facility}
                                providerName={provider.providerName}
                                getCapacityStatus={getCapacityStatus}
                              />
                            </Box>
                          )
                        })}
                      </Box>
                    </Collapse>
                  </Box>
                )
              })}
            </Box>
          </Paper>
        </Box>
      </Box>
    </Box>
  )
}
